import { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';

import { useAgentId, useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useShare, type ShareOutcome } from '../lib/use-share.js';
import { logger } from '../lib/logger.js';
import { agentsRepo, platformBrandsRepo } from '../lib/repositories/earnings-repo.js';
import type { AgentProfile, PlatformBrand } from '@kilat/shared';

/**
 * /code — the agent's "personal version" of the public marketing
 * `/a/{code}` page. Big visible code, scannable QR, print button for
 * laminated cards, share button for WA / SMS / native share.
 *
 * Reads the agent profile (for `code` + `name` + `platformBrandId`)
 * and the agent's platform brand (for `domains.marketing`, which we
 * need to compose the brand-correct signup URL). Both are subscribed
 * — a super-admin renaming the brand or rotating domains updates the
 * QR within seconds.
 *
 * The deep-link URL is `https://{brand.domains.marketing}/signup?ref={code}`
 * matching the public `/a/{code}` page's encoding. An operator who
 * scans lands on the brand-correct marketing signup form with the
 * code pre-filled and the agent's name shown (Sprint 17).
 *
 * Print-friendly: `@media print` strips the page chrome so the card
 * fills the page when the agent hits Cmd/Ctrl+P.
 */
export function CodePage() {
  const { user, loading: authLoading } = useAuth();
  const agentId = useAgentId();
  const t = useI18n();
  const platformBrand = usePlatformBrand();
  const [agent, setAgent] = useState<AgentProfile | null>(null);
  // `agentReady` distinguishes "first snapshot hasn't arrived" from "arrived as
  // null" (missing/unparseable doc). Without it a null agent read hung the page
  // on a permanent skeleton; now it resolves to an explicit gone state with a
  // Try-again ([[feedback-no-dead-ends]]).
  const [agentReady, setAgentReady] = useState(false);
  const [brand, setBrand] = useState<PlatformBrand | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const { outcome, share, copy } = useShare();

  const retry = () => {
    setError(null);
    setAgent(null);
    setAgentReady(false);
    setBrand(null);
    setRetryNonce((n) => n + 1);
  };

  // Live agent subscription so a code re-issue / rename propagates. onError
  // routes a denied/dropped read to the error state instead of hanging.
  useEffect(() => {
    if (!agentId) {
      setAgent(null);
      setAgentReady(false);
      return undefined;
    }
    const unsub = agentsRepo.snapshot(
      agentId,
      (v) => {
        setAgent(v);
        setAgentReady(true);
      },
      (e) => setError(e.message),
    );
    return unsub;
  }, [agentId, retryNonce]);

  // Live brand subscription so a domain rotation propagates.
  useEffect(() => {
    if (!agent?.platformBrandId) {
      setBrand(null);
      return undefined;
    }
    const unsub = platformBrandsRepo.snapshot(
      agent.platformBrandId,
      (v) => {
        if (!v) {
          setError(`Brand "${agent.platformBrandId}" not found.`);
          return;
        }
        setError(null);
        setBrand(v);
      },
      (e) => setError(e.message),
    );
    return unsub;
  }, [agent?.platformBrandId, retryNonce]);

  const signupUrl = useMemo(() => {
    if (!brand || !agent) return null;
    return `https://${brand.domains.marketing}/signup?ref=${encodeURIComponent(agent.code)}`;
  }, [brand, agent]);

  // ── states ──────────────────────────────────────────────────────────
  if (authLoading) {
    return (
      <PageWrap>
        <CardSkeleton />
      </PageWrap>
    );
  }
  if (!user) {
    return (
      <PageWrap>
        <EmptyCard
          title={t.t('agent.payouts.signedOutTitle')}
          body={t.t('agent.code.signedOutBody')}
        />
      </PageWrap>
    );
  }
  if (!agentId) {
    return (
      <PageWrap>
        <EmptyCard
          title={t.t('agent.payouts.notAgentTitle')}
          body={t.t('agent.code.notAgentBody')}
          ctaLabel={t.t('agent.payouts.notAgentCta')}
          ctaHref={`https://${platformBrand.domains.marketing}/become-an-agent`}
        />
      </PageWrap>
    );
  }
  if (error) {
    return (
      <PageWrap>
        <EmptyCard
          title={t.t('agent.cardGone.agentTitle')}
          body={t.t('agent.cardGone.body')}
          onRetry={retry}
          retryLabel={t.t('agent.cardGone.retry')}
        />
      </PageWrap>
    );
  }
  if (agentReady && !agent) {
    return (
      <PageWrap>
        <EmptyCard
          title={t.t('agent.profileGone.agentTitle')}
          body={t.t('agent.profileGone.body')}
          onRetry={retry}
          retryLabel={t.t('agent.profileGone.retry')}
        />
      </PageWrap>
    );
  }
  if (!agent || !brand || !signupUrl) {
    return (
      <PageWrap>
        <CardSkeleton />
      </PageWrap>
    );
  }

  return (
    <PageWrap>
      {/* Phone: the actions, then the card. Tablet and up: side by side. */}
      <div className="ag-pair">
        <header className="no-print" style={{ marginBottom: 'var(--space-2)' }}>
          <h1 style={{ margin: 0, fontSize: 'var(--text-h1)', fontWeight: 600 }}>
            {t.t('agent.code.shareCardTitle')}
          </h1>
          <p
            style={{
              margin: 'var(--space-2) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t.t('agent.code.shareCardIntro')}
          </p>
          <div
            style={{
              marginTop: 'var(--space-4)',
              display: 'flex',
              gap: 'var(--space-2)',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              onClick={() => {
                void share({
                  title: t.t('agent.code.shareTitle', { brand: brand.name, name: agent.name }),
                  text: t.t('agent.code.shareText', { brand: brand.name }),
                  url: signupUrl,
                });
              }}
              style={primaryButton}
            >
              {t.t('agent.code.shareLink')}
            </button>
            <button
              type="button"
              onClick={() => {
                void copy(signupUrl);
              }}
              style={secondaryButton}
            >
              {t.t('agent.code.copyLink')}
            </button>
            <button type="button" onClick={() => window.print()} style={secondaryButton}>
              {t.t('agent.code.print')}
            </button>
            <ShareOutcomeNote outcome={outcome} />
          </div>
        </header>

        <ShareCard
          url={signupUrl}
          brandName={brand.name}
          agentName={agent.name}
          code={agent.code}
        />
      </div>
    </PageWrap>
  );
}

// ── share card ──────────────────────────────────────────────────────────

function ShareCard({
  url,
  brandName,
  agentName,
  code,
  size = 320,
}: {
  readonly url: string;
  readonly brandName: string;
  readonly agentName: string;
  readonly code: string;
  readonly size?: number;
}) {
  const t = useI18n();
  const [svgMarkup, setSvgMarkup] = useState<string | null>(null);
  const [drawError, setDrawError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSvgMarkup(null);
    setDrawError(false);
    QRCode.toString(url, {
      type: 'svg',
      errorCorrectionLevel: 'H',
      margin: 1,
      color: { dark: '#0f172a', light: '#ffffff' },
    })
      .then((svg) => {
        if (cancelled) return;
        setSvgMarkup(svg);
      })
      .catch((e) => {
        logger.error('agent qr generation failed', { url, error: String(e) });
        if (cancelled) return;
        setDrawError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <article
      className="agent-share-card"
      style={{
        width: '100%',
        maxWidth: 480,
        padding: 'var(--space-6)',
        borderRadius: 'var(--radius-lg)',
        background: '#ffffff',
        color: '#0f172a',
        border: '1px solid var(--color-border)',
        boxShadow: 'var(--shadow-2)',
        textAlign: 'center',
        display: 'grid',
        gap: 'var(--space-4)',
        margin: '0 auto',
      }}
    >
      <header>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            letterSpacing: 4,
            textTransform: 'uppercase',
            color: 'rgba(15,23,42,0.55)',
            margin: 0,
          }}
        >
          {t.t('agent.code.salesAgentEyebrow', { brand: brandName })}
        </p>
        <h2 style={{ fontSize: 'var(--text-h2)', fontWeight: 600, margin: 'var(--space-2) 0 0 0' }}>
          {agentName}
        </h2>
        <p
          style={{
            fontSize: 'var(--text-body)',
            color: 'rgba(15,23,42,0.7)',
            margin: 'var(--space-2) 0 0 0',
          }}
        >
          {t.t('agent.code.scanFreeTrial')}
        </p>
      </header>

      <div
        aria-label={t.t('agent.code.qrAria')}
        style={{
          width: size,
          height: size,
          margin: '0 auto',
          background: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        dangerouslySetInnerHTML={svgMarkup ? { __html: svgMarkup } : undefined}
      >
        {svgMarkup ? null : (
          <span style={{ fontSize: 'var(--text-meta)', color: 'rgba(15,23,42,0.45)' }}>
            {drawError ? t.t('agent.code.drawError') : t.t('agent.code.drawingQr')}
          </span>
        )}
      </div>

      <footer style={{ display: 'grid', gap: 'var(--space-1)' }}>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'rgba(15,23,42,0.55)',
            margin: 0,
          }}
        >
          {t.t('agent.code.agentCodeLabel')}
        </p>
        <p
          style={{
            fontSize: 'var(--text-h2)',
            fontWeight: 600,
            letterSpacing: 2,
            fontFamily: 'ui-monospace, monospace',
            margin: 0,
          }}
        >
          {code}
        </p>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'rgba(15,23,42,0.55)',
            margin: 'var(--space-2) 0 0 0',
          }}
        >
          {t.t('agent.code.orVisit')}
        </p>
        <p
          style={{
            fontSize: 'var(--text-body)',
            wordBreak: 'break-all',
            margin: 0,
          }}
        >
          {humanUrl(url)}
        </p>
      </footer>

      <style>{`
        @media print {
          @page { margin: 16mm; }
          body { background: #ffffff !important; }
          .agent-share-card {
            box-shadow: none !important;
            border: none !important;
            page-break-inside: avoid;
          }
        }
      `}</style>
    </article>
  );
}

function humanUrl(url: string): string {
  try {
    const u = new URL(url);
    return `${u.host}${u.pathname === '/' ? '' : u.pathname}${u.search}`;
  } catch {
    return url;
  }
}

// ── shell + states ──────────────────────────────────────────────────────

function PageWrap({ children }: { readonly children: React.ReactNode }) {
  return (
    <main
      className="agent-code-page ag-page"
      style={{
        minHeight: '100dvh',
        background: 'var(--tint-subtle)',
        padding: 'var(--space-4) var(--space-4) var(--space-12) var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
        margin: '0 auto',
      }}
    >
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .agent-code-page {
            padding: 0 !important;
            min-height: auto !important;
            background: #ffffff !important;
          }
        }
      `}</style>
      {children}
    </main>
  );
}

function ShareOutcomeNote({ outcome }: { readonly outcome: ShareOutcome }) {
  if (outcome === 'idle' || outcome === 'cancelled') return null;
  const text =
    outcome === 'shared'
      ? 'Shared.'
      : outcome === 'copied'
        ? 'Copied to clipboard.'
        : 'Could not share or copy. Long-press the link below to copy manually.';
  return (
    <span
      role="status"
      style={{
        alignSelf: 'center',
        fontSize: 'var(--text-meta)',
        color: outcome === 'failed' ? '#b45309' : 'var(--color-primary)',
      }}
    >
      {text}
    </span>
  );
}

function CardSkeleton() {
  const t = useI18n();
  return (
    <div
      style={{
        width: '100%',
        maxWidth: 480,
        height: 540,
        margin: '0 auto',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--color-border)',
      }}
      aria-busy="true"
      aria-label={t.t('agent.code.loadingCardAria')}
    />
  );
}

function EmptyCard({
  title,
  body,
  ctaLabel,
  ctaHref,
  onRetry,
  retryLabel,
}: {
  readonly title: string;
  readonly body: string;
  readonly ctaLabel?: string;
  readonly ctaHref?: string;
  readonly onRetry?: () => void;
  readonly retryLabel?: string;
}) {
  return (
    <section
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        textAlign: 'center',
        boxShadow: 'var(--shadow-1)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h2>
      <p
        style={{
          margin: 'var(--space-3) auto 0 auto',
          maxWidth: 420,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        {body}
      </p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          style={{
            ...primaryButton,
            marginTop: 'var(--space-4)',
            minHeight: 'var(--touch-target, 44px)',
          }}
        >
          {retryLabel}
        </button>
      ) : null}
      {ctaLabel && ctaHref ? (
        <a href={ctaHref} style={{ ...primaryButton, marginTop: 'var(--space-4)' }}>
          {ctaLabel}
        </a>
      ) : null}
    </section>
  );
}

const primaryButton: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--space-3) var(--space-6)',
  borderRadius: 'var(--radius-pill)',
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: 'var(--text-meta)',
  border: 'none',
  cursor: 'pointer',
};

const secondaryButton: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--space-3) var(--space-6)',
  borderRadius: 'var(--radius-pill)',
  background: 'transparent',
  color: 'var(--color-on-surface)',
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: 'var(--text-meta)',
  border: '1px solid var(--color-border-strong)',
  cursor: 'pointer',
};
